import type { Band, MissionId } from '@badger-sentinel/rules';
import {
  BANDS,
  DELTAV_COSTS,
  isCyberMission,
  missionDef,
  transferCost,
  type TargetKind,
} from '@badger-sentinel/rules';
import type { CellViewWire, OrderDraft, OrderTargetWire } from '@badger-sentinel/protocol';

import { byAlias } from './alias.js';
import { missionLabel, missionSummary, paramSpecOf, targetsOwnAsset } from './missions.js';
import type { FriendlyAssetViewWire } from './view-types.js';

/**
 * The three-slot order builder (design 1458–1540).
 *
 * ACTOR · MISSION · TARGET, and a fourth thing the design does not draw — a
 * mission parameter, which lives inside the mission slot (`model/missions.ts`
 * says why). Everything here is pure: the store holds a `BuilderDraft`, this
 * module turns it into option lists, a legality reading and an `OrderDraft`,
 * and the components render what it returns.
 *
 * ## The mission list is a three-way filter and all three sources are real
 *
 * 1. `asset.assignedCell === view.viewer.cell` — §3.3 invariant 2, surfaced as
 *    `orderable`. A seat is offered missions for the assets it may task.
 * 2. `asset.missions[]` — what this type can do at all (RB §4.3).
 * 3. `MissionDef.targetKinds` — against whatever is in the target slot.
 *
 * A mission that survives all three is offered; one that does not is **not
 * rendered**, disabled or otherwise. If that leaves nothing the picker says so
 * once, in the design's own words.
 *
 * ## Nothing here decides legality
 *
 * The picker's job is to stop the seat building an order the *wire* cannot
 * carry. Whether the order is *legal* is `model/ladder.ts`'s reading and the
 * server's decision (§7.3) — and those two disagree by design, because the
 * client is reading a belief and the server revalidates.
 */

export type SlotId = 'asset' | 'mission' | 'target' | 'param';

export const SLOT_ORDER: readonly SlotId[] = Object.freeze(['asset', 'mission', 'target'] as const);

export type ParamValue = string | number | boolean;

export interface BuilderDraft {
  readonly asset: string | null;
  readonly mission: MissionId | null;
  /** `null` is an empty slot. `{kind:'NONE'}` is a target the seat chose to leave open. */
  readonly target: OrderTargetWire | null;
  readonly params: Readonly<Record<string, ParamValue>>;
}

export const EMPTY_DRAFT: BuilderDraft = Object.freeze({
  asset: null,
  mission: null,
  target: null,
  params: Object.freeze({}),
});

// ---------------------------------------------------------------------------
// Picker rows
// ---------------------------------------------------------------------------

/** The three columns the design draws. What a row *sets* is on the union below. */
interface PickRowBase {
  readonly key: string;
  /** The mono column — an id, an alias, a band, a mission name. */
  readonly id: string;
  /** The prose column. */
  readonly desc: string;
  /** The right-hand column: a cost, a rung, a slot count. Empty when there is none. */
  readonly meta: string;
  readonly tone: 'friendly' | 'hostile' | 'mission' | 'neutral';
}

export interface AssetPickRow extends PickRowBase {
  readonly slot: 'asset';
  readonly asset: string;
}
export interface MissionPickRow extends PickRowBase {
  readonly slot: 'mission';
  readonly mission: MissionId;
}
export interface TargetPickRow extends PickRowBase {
  readonly slot: 'target';
  readonly target: OrderTargetWire;
}
export interface ParamPickRow extends PickRowBase {
  readonly slot: 'param';
  readonly value: ParamValue;
}

/**
 * A picker row, discriminated by the slot it fills.
 *
 * A union rather than a base type with a cast, because the picker is one
 * component over four different lists and the *only* thing that differs is what
 * choosing a row does. A cast at the call site would put the decision in two
 * places and let a target row be applied to the asset slot without complaint.
 */
export type PickRow = AssetPickRow | MissionPickRow | TargetPickRow | ParamPickRow;

/** What a chosen row does. The four store writes, handed in by the caller. */
export interface PickSink {
  readonly asset: (id: string) => void;
  readonly mission: (mission: MissionId) => void;
  readonly target: (target: OrderTargetWire) => void;
  readonly param: (value: ParamValue) => void;
}

export function applyPick(row: PickRow, sink: PickSink): void {
  switch (row.slot) {
    case 'asset':
      sink.asset(row.asset);
      return;
    case 'mission':
      sink.mission(row.mission);
      return;
    case 'target':
      sink.target(row.target);
      return;
    case 'param':
      sink.param(row.value);
  }
}

function assetOf(view: CellViewWire, id: string | null): FriendlyAssetViewWire | null {
  if (id === null) return null;
  return view.friendly.find((candidate) => candidate.id === id) ?? null;
}

/** The assets this seat may task — invariant 2, read from the asset and not the mission. */
export function orderableAssets(view: CellViewWire): readonly FriendlyAssetViewWire[] {
  return view.friendly.filter(
    (asset) => asset.orderable && asset.assignedCell === view.viewer.cell,
  );
}

/**
 * Missions offerable for an asset, given whatever is in the target slot.
 *
 * Three conditions, all of them real (see the header). The fourth — Phase 3's
 * `FRIENDLY_TARGET_MISSIONS`, which withheld eight missions the wire could not
 * express — is **gone**: Phase 6.5 landed `zOrderTarget`'s `OWN_ASSET` arm and
 * the eight are offered like any others. What is left of that set is
 * `targetsOwnAsset`, which no longer decides whether a mission appears but which
 * *list* its target slot draws from.
 *
 * `def.targetKinds` is the **domain's** vocabulary and says `CONTACT` for all
 * eight, because the domain has no `OWN_ASSET` and does not need one — the
 * server translates. So the match below is against `targetKindOf`, which reads a
 * wire target back into the domain kind the mission table speaks.
 */
export function missionsFor(
  view: CellViewWire,
  asset: FriendlyAssetViewWire,
  target: OrderTargetWire | null,
): readonly MissionId[] {
  void view;
  return asset.missions.filter((mission) => {
    const def = missionDef(mission);
    if (def === undefined) return false;
    if (target === null) return true;
    if (!(def.targetKinds as readonly TargetKind[]).includes(targetKindOf(target))) return false;
    // A friendly id in the slot only fits a mission that acts on friendly kit,
    // and an alias only fits one that does not. Without this the two kinds
    // collapse into `CONTACT` and the picker offers `ENGAGE` against one's own
    // TT&C station — an order the server would take and the engine would drop.
    return targetsOwnAsset(mission) === (target.kind === 'OWN_ASSET');
  });
}

/**
 * The domain kind a wire target resolves to — the one place the two vocabularies
 * are reconciled on this side.
 *
 * `OWN_ASSET` is a wire arm with no domain counterpart (Q24): the server turns
 * it into a `CONTACT` carrying a true `AssetId`, which is what `MissionDef.
 * targetKinds` has always meant for the eight friendly-target missions. Reading
 * it any other way would have every one of them fail the `targetKinds` check
 * against a table that is correct.
 */
export function targetKindOf(target: OrderTargetWire): TargetKind {
  return target.kind === 'OWN_ASSET' ? 'CONTACT' : target.kind;
}

export function assetPickRows(view: CellViewWire, draft: BuilderDraft): readonly AssetPickRow[] {
  const mission = draft.mission;
  return orderableAssets(view)
    .filter((asset) =>
      mission === null ? true : missionsFor(view, asset, draft.target).includes(mission),
    )
    .map((asset) => ({
      key: asset.id,
      id: asset.id,
      desc: asset.typeName,
      meta:
        String(asset.missionsThisTurn.length) +
        ' / ' +
        String(asset.capacity) +
        (asset.status === 'NOMINAL' ? '' : ' · ' + asset.status),
      tone: 'friendly' as const,
      slot: 'asset' as const,
      asset: asset.id,
    }));
}

export function missionPickRows(
  view: CellViewWire,
  draft: BuilderDraft,
): readonly MissionPickRow[] {
  const chosen = assetOf(view, draft.asset);
  const pool = chosen === null ? orderableAssets(view) : [chosen];
  const offered = new Set<MissionId>();
  for (const asset of pool) {
    for (const mission of missionsFor(view, asset, draft.target)) offered.add(mission);
  }
  // Table order, so the list is the same shape every time it opens — `asset.missions`
  // is already in the type table's order and a set iterates in insertion order.
  return [...offered].map((mission) => {
    const def = missionDef(mission);
    return {
      key: mission,
      id: missionLabel(mission),
      desc: missionSummary(mission),
      meta: def === undefined ? '' : def.cell,
      tone: 'mission' as const,
      slot: 'mission' as const,
      mission,
    };
  });
}

/**
 * Target rows for the chosen mission, one block per `targetKind` it accepts.
 *
 * A `SLOT` list is generated against the mission's own reach — `PHASE_SHIFT`
 * lists the slots it can afford in its own band, `TRANSFER` lists the bands the
 * transfer table connects — because 24 slots × 4 bands is a list nobody reads,
 * and the reach is a **reading of `DELTAV_COSTS` and `TRANSFER_TABLE`**, not a
 * second implementation of the rule.
 */
export function targetPickRows(view: CellViewWire, draft: BuilderDraft): readonly TargetPickRow[] {
  const mission = draft.mission;
  if (mission === null) return [];
  const def = missionDef(mission);
  if (def === undefined) return [];
  const actor = assetOf(view, draft.asset);

  const rows: TargetPickRow[] = [];
  for (const kind of def.targetKinds) {
    switch (kind) {
      case 'NONE':
        rows.push({
          key: 'NONE',
          id: 'NO TARGET',
          desc: '',
          meta: '',
          tone: 'neutral',
          slot: 'target',
          target: { kind: 'NONE' },
        });
        break;

      case 'SELF':
        if (actor !== null) {
          rows.push({
            key: 'SELF',
            id: actor.id,
            desc: actor.typeName,
            meta: 'SELF',
            tone: 'friendly',
            slot: 'target',
            target: { kind: 'SELF' },
          });
        }
        break;

      case 'BAND':
        for (const band of BANDS) {
          rows.push({
            key: 'BAND:' + band,
            id: band,
            desc: String(view.map.bandSlots[band]) + ' SLOTS',
            meta: 'DEBRIS ' + String(view.theatre.debris[band]),
            tone: 'neutral',
            slot: 'target',
            target: { kind: 'BAND', band },
          });
        }
        break;

      case 'REGION':
        for (const region of view.map.regions) {
          rows.push({
            key: 'REGION:' + region.id,
            id: region.id,
            desc: region.name,
            meta: view.theatre.weather[region.id] ?? '',
            tone: 'neutral',
            slot: 'target',
            target: { kind: 'REGION', region: region.id },
          });
        }
        break;

      case 'CONTACT':
        rows.push(...contactRows(view, mission));
        break;

      case 'SLOT':
        rows.push(...slotRows(view, mission, actor));
        break;

      case 'MW_EVENT':
        // RB §5.7.5 — an interception needs a *released* track, and the release is
        // the only thing that put this event id in front of Joint Fires.
        for (const release of view.mwReleases) {
          if (!release.interceptable) continue;
          rows.push({
            key: 'MW:' + release.eventId,
            id: release.eventId,
            desc: release.assignedIdentity,
            meta:
              'STAGE ' +
              String(release.releasedAtStage) +
              ' · [' +
              (release.stageModifier >= 0 ? '+' : '') +
              String(release.stageModifier) +
              ']',
            tone: 'hostile',
            slot: 'target',
            target: { kind: 'MW_EVENT', event: release.eventId },
          });
        }
        break;
    }
  }
  return rows;
}

/**
 * The object rows for a mission whose target kind is `CONTACT` — which on the
 * wire is two different arms and three different lists.
 *
 * **A friendly-target mission draws own assets and nothing else** (Q24). The rows
 * carry `{kind:'OWN_ASSET', asset}` and are ordered by id, because an own asset
 * has a true id and there is nothing to hide about the order of one's own order
 * of battle.
 *
 * **Everything else draws aliases**, ordered by `hash(alias)` — array position is
 * a channel (must-not #5), and the picker is one more list that must not carry
 * one. The board's `contacts[]` is the usual source; a **cyber** mission also
 * reaches `cyber.contacts[]`, which is a different set: RB §5.6.2's parenthesis
 * is load-bearing, and a machine a foothold revealed is *"visible (to cyber; not
 * FOUND)"*. Offering one to `ENGAGE` would turn cyber access into a firing
 * solution nobody bought, so the two sets are merged for cyber missions only and
 * deduped on the alias — which is the same string in both lists by construction
 * (`CyberContactView.alias`).
 */
function contactRows(view: CellViewWire, mission: MissionId): readonly TargetPickRow[] {
  if (targetsOwnAsset(mission)) {
    return [...view.friendly]
      .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
      .map((asset) => ({
        key: 'OWN:' + asset.id,
        id: asset.id,
        desc: asset.typeName,
        meta: asset.status === 'NOMINAL' ? asset.assignedCell : asset.status,
        tone: 'friendly' as const,
        slot: 'target' as const,
        target: { kind: 'OWN_ASSET' as const, asset: asset.id },
      }));
  }

  const known = new Map(view.contacts.map((contact) => [contact.id, contact]));
  const aliases = new Set(known.keys());
  if (isCyberMission(mission)) for (const row of view.cyber.contacts) aliases.add(row.alias);

  return byAlias([...aliases], (alias) => alias).map((alias) => {
    const contact = known.get(alias);
    const cyber = view.cyber.contacts.find((row) => row.alias === alias);
    return {
      key: 'CONTACT:' + alias,
      id: alias,
      // A cyber-revealed machine has no presented type — nothing looked at it —
      // so what it says instead is the enclave it is on, which is what cyber
      // knows and the only thing it was told.
      desc: contact?.presentedTypeName ?? (cyber === undefined ? '' : cyber.network),
      meta: contact?.rung ?? (cyber === undefined ? '' : 'CYBER · NOT FOUND'),
      tone: 'hostile' as const,
      slot: 'target' as const,
      target: { kind: 'CONTACT' as const, contact: alias },
    };
  });
}

function slotRows(
  view: CellViewWire,
  mission: MissionId,
  actor: FriendlyAssetViewWire | null,
): readonly TargetPickRow[] {
  if (actor === null || actor.location.kind !== 'ORBIT') return [];
  const from: Band = actor.location.band;
  const rows: TargetPickRow[] = [];

  if (mission === 'PHASE_SHIFT') {
    const size = view.map.bandSlots[from];
    for (let step = 1; step <= DELTAV_COSTS.PHASE_SHIFT_MAX_SLOTS; step += 1) {
      for (const direction of [1, -1]) {
        const slot = wrapSlot(actor.location.slot + direction * step, size);
        rows.push({
          key: 'SLOT:' + from + ':' + String(slot),
          id: from + ' ' + String(slot),
          desc: String(step) + (step === 1 ? ' SLOT' : ' SLOTS'),
          meta: String(step * DELTAV_COSTS.PHASE_SHIFT_PER_SLOT) + ' M/S',
          tone: 'neutral',
          slot: 'target',
          target: { kind: 'SLOT', band: from, slot },
        });
      }
    }
    return dedupe(rows);
  }

  for (const band of BANDS) {
    const cost = transferCost(from, band);
    if (cost === null) continue;
    const size = view.map.bandSlots[band];
    for (let slot = 1; slot <= size; slot += 1) {
      rows.push({
        key: 'SLOT:' + band + ':' + String(slot),
        id: band + ' ' + String(slot),
        desc: String(cost.turns) + ' TURNS',
        meta: String(cost.cost) + ' M/S',
        tone: 'neutral',
        slot: 'target',
        target: { kind: 'SLOT', band, slot },
      });
    }
  }
  return rows;
}

function wrapSlot(slot: number, size: number): number {
  const zeroBased = (((slot - 1) % size) + size) % size;
  return zeroBased + 1;
}

function dedupe(rows: readonly TargetPickRow[]): readonly TargetPickRow[] {
  const seen = new Set<string>();
  return rows.filter((row) => (seen.has(row.key) ? false : (seen.add(row.key), true)));
}

/**
 * The rows a given slot's picker offers.
 *
 * One function, two callers: the panel renders them and the shell's `1-9`
 * binding takes the *n*th. Two lists computed separately would drift the first
 * time a filter changed on one side only.
 */
export function pickRowsFor(
  view: CellViewWire,
  draft: BuilderDraft,
  slot: SlotId,
): readonly PickRow[] {
  switch (slot) {
    case 'asset':
      return assetPickRows(view, draft);
    case 'mission':
      return missionPickRows(view, draft);
    case 'target':
      return targetPickRows(view, draft);
    case 'param':
      return paramPickRows(draft.mission);
  }
}

/**
 * Why a picker has no rows — **one place asking one question**.
 *
 * The design's sentence is "No valid options for this combination.", and where
 * the seat has assets it is exactly right: the row set is the three-way filter of
 * asset × mission × target, and the operator's next move is to change a slot.
 *
 * At a seat with **nothing to task** it is a lie dressed as a hint. CREWCOM
 * commands and does not task (RB §5.1, §3.3 invariant 3), so `orderableAssets`
 * returns zero for it *by rule* — and every slot empties with it, not just the
 * asset one: `missionPickRows` pools over the same list, and target and param
 * both need a mission. No combination exists that would fill any of them, and
 * telling that operator to try another sends them round a loop with no exit.
 *
 * So the question is asked once, of the seat rather than of the slot, and the
 * answer is derived from the one condition that separates the two cases. A seat
 * that *is* handed assets and has filtered them away still gets the combination
 * sentence, which is what makes this a statement about the seat and not a special
 * case for a cell name.
 */
export function pickerEmptyReason(view: CellViewWire): string {
  return orderableAssets(view).length === 0
    ? 'No assets assigned to this cell.'
    : 'No valid options for this combination.';
}

/**
 * The picker's own text filter (the design's `bs-pick` box).
 *
 * Here rather than in the component because the `1-9` hotkey has to take the
 * *n*th **visible** row, and the shell binds the key while the panel renders the
 * list — two places that must agree on what "visible" means.
 */
export function filterPickRows<T extends PickRow>(rows: readonly T[], query: string): readonly T[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return rows;
  return rows.filter((row) =>
    (row.id + ' ' + row.desc + ' ' + row.meta).toLowerCase().includes(needle),
  );
}

export function paramPickRows(mission: MissionId | null): readonly ParamPickRow[] {
  if (mission === null) return [];
  const spec = paramSpecOf(mission);
  if (spec === null) return [];
  return spec.options.map((option) => ({
    key: String(option.value),
    id: option.label,
    desc: option.detail,
    meta: '',
    tone: 'neutral' as const,
    slot: 'param' as const,
    value: option.value,
  }));
}

// ---------------------------------------------------------------------------
// Delta-V, read from the shared tables
// ---------------------------------------------------------------------------

/**
 * What a manoeuvre costs this asset, or `null` for a target it cannot reach.
 *
 * A reading of `MissionDef.deltaV`, `DELTAV_COSTS` and `TRANSFER_TABLE` — the
 * same three the engine reads (`validate.ts` `deltaVCostOf`). The ring size
 * comes from `view.map.bandSlots` rather than a constant, because HEO is 4.
 */
export function deltaVCostFor(
  view: CellViewWire,
  asset: FriendlyAssetViewWire,
  mission: MissionId,
  target: OrderTargetWire | null,
): number | null {
  const cost = missionDef(mission)?.deltaV ?? null;
  if (cost === null) return 0;
  switch (cost.kind) {
    case 'FIXED':
      return cost.amount;
    case 'PER_SLOT': {
      if (target === null || target.kind !== 'SLOT') return null;
      if (asset.location.kind !== 'ORBIT' || asset.location.band !== target.band) return null;
      const size = view.map.bandSlots[target.band];
      const raw = Math.abs(asset.location.slot - target.slot) % size;
      const slots = Math.min(raw, size - raw);
      if (slots === 0 || slots > cost.maxSlots) return null;
      return slots * cost.amount;
    }
    case 'TRANSFER_TABLE': {
      if (target === null || target.kind !== 'SLOT') return null;
      const from = asset.location.kind === 'ORBIT' ? asset.location.band : null;
      if (from === null) return null;
      return transferCost(from, target.band)?.cost ?? null;
    }
  }
}

// ---------------------------------------------------------------------------
// The draft, as the wire wants it
// ---------------------------------------------------------------------------

/**
 * `BuilderDraft` → `OrderDraft`, or `null` when the two mandatory slots are empty.
 *
 * **No `team`, no `cell`** — §3.3 invariant 2: they come from the seat token and
 * a client that could write them could claim to be another cell. An empty target
 * slot becomes `{kind:'NONE'}`, which is a real wire value and, for every mission
 * in this build, an illegal one — `blockFor` says so rather than this function
 * refusing to build it, so the seat sees *why* rather than a control that does
 * nothing.
 */
export function toOrderDraft(draft: BuilderDraft): OrderDraft | null {
  if (draft.asset === null || draft.mission === null) return null;
  const params = Object.keys(draft.params).length === 0 ? undefined : draft.params;
  return {
    asset: draft.asset,
    mission: draft.mission,
    target: draft.target ?? { kind: 'NONE' },
    ...(params === undefined ? {} : { params }),
  };
}

/** An `OrderDraft` back into the builder, for EDIT and REASSIGN. */
export function fromOrderDraft(order: OrderDraft): BuilderDraft {
  return {
    asset: order.asset,
    mission: order.mission,
    target: order.target,
    params: order.params ?? {},
  };
}

/**
 * Which slot an object fills when it is ⇧-clicked or dropped on the strip.
 *
 * One of the viewer's own assets is an actor; anything else is a target. The
 * design's own rule, and the reason ⌥-click exists at all — a friendly asset can
 * be a target (Electronic Protection, TT&C) and the operator needs a way to say
 * so even where this build cannot yet carry it.
 */
export function naturalSlotFor(view: CellViewWire, id: string): SlotId {
  return orderableAssets(view).some((asset) => asset.id === id) ? 'asset' : 'target';
}

/**
 * The wire target an arbitrary roster subject becomes. `null` if it cannot be one.
 *
 * The order of the four arms is the order of specificity, and only the last two
 * are new. **A friendly asset is an `OWN_ASSET`** (Q24): before Phase 6.5 this
 * returned `null` for one, which is why ⌥-clicking one's own TT&C station used
 * to say `CANNOT BE A TARGET`. **A cyber-revealed machine is a `CONTACT`**,
 * because that is exactly what it is — an alias for something the seat was never
 * told the identity of — and it is checked separately because RB §5.6.2 keeps it
 * out of `contacts[]`.
 *
 * Which of the two object arms a mission will accept is `missionsFor`'s
 * question, not this one's: this says what a subject *is*, and the picker says
 * what fits.
 */
export function targetForSubject(view: CellViewWire, id: string): OrderTargetWire | null {
  if (view.contacts.some((contact) => contact.id === id)) return { kind: 'CONTACT', contact: id };
  if (view.cyber.contacts.some((contact) => contact.alias === id)) {
    return { kind: 'CONTACT', contact: id };
  }
  if (view.friendly.some((asset) => asset.id === id)) return { kind: 'OWN_ASSET', asset: id };
  if (view.map.regions.some((region) => region.id === id)) return { kind: 'REGION', region: id };
  if ((BANDS as readonly string[]).includes(id)) return { kind: 'BAND', band: id as Band };
  return null;
}

/** How a filled slot reads in the strip: the mono id and the prose beside it. */
export function targetWords(
  view: CellViewWire,
  target: OrderTargetWire | null,
): {
  readonly id: string;
  readonly sub: string;
} {
  if (target === null) return { id: '—', sub: '' };
  switch (target.kind) {
    case 'NONE':
      return { id: '—', sub: 'NO TARGET' };
    case 'SELF':
      return { id: 'SELF', sub: '' };
    case 'BAND':
      return { id: target.band, sub: 'BAND' };
    case 'REGION':
      return {
        id: target.region,
        sub: view.map.regions.find((region) => region.id === target.region)?.name ?? '',
      };
    case 'SLOT':
      return { id: target.band + ' ' + String(target.slot), sub: 'SLOT' };
    case 'CONTACT':
      return {
        id: target.contact,
        sub:
          view.contacts.find((contact) => contact.id === target.contact)?.presentedTypeName ??
          // A cyber-revealed machine is not in `contacts[]` at all, and the
          // enclave is the only thing the wire says about it.
          view.cyber.contacts.find((row) => row.alias === target.contact)?.network ??
          '',
      };
    case 'OWN_ASSET':
      return {
        id: target.asset,
        sub: view.friendly.find((asset) => asset.id === target.asset)?.typeName ?? '',
      };
    case 'MW_EVENT':
      return {
        id: target.event,
        sub:
          view.mwReleases.find((release) => release.eventId === target.event)?.assignedIdentity ??
          '',
      };
  }
}

/** The band an order touches, for the debris row. */
export function bandOfOrder(
  view: CellViewWire,
  asset: FriendlyAssetViewWire | null,
  target: OrderTargetWire | null,
): Band | null {
  if (target !== null && (target.kind === 'BAND' || target.kind === 'SLOT')) return target.band;
  if (target !== null && target.kind === 'CONTACT') {
    const contact = view.contacts.find((candidate) => candidate.id === target.contact);
    if (
      contact !== undefined &&
      (contact.location.kind === 'BAND' || contact.location.kind === 'SLOT')
    ) {
      return contact.location.band;
    }
  }
  if (asset !== null && asset.location.kind === 'ORBIT') return asset.location.band;
  return null;
}

/**
 * The region an order lands in, for the weather row.
 *
 * **The target's region, never the actor's** — `validate.ts`'s `regionOfTarget`
 * is what the server checks, and RB §8.3's rule is about weather *over the
 * region being collected against*. A ground radar in AURELIA looking at a LEO
 * contact has no weather row at all, which is right: weather does not bear on an
 * orbital engagement under these rules. Falling back to the actor's own site
 * would have drawn a number that looks like it counts and does not.
 */
export function regionOfOrder(view: CellViewWire, target: OrderTargetWire | null): string | null {
  if (target === null) return null;
  if (target.kind === 'REGION') return target.region;
  if (target.kind === 'CONTACT') {
    const contact = view.contacts.find((candidate) => candidate.id === target.contact);
    if (contact !== undefined && contact.location.kind === 'REGION') return contact.location.region;
  }
  return null;
}

export { assetOf };
